import type { FC } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';

import { Checkbox } from '../../../../components/Checkbox';
import type { ContactStatusFilterValue } from '../../../../domain/contact';
import { StyledFilterSummary, StyledFilterName, StyledNotEmptyIndicator } from '../styles';

import { StyledCheckbox, StyledRevealedGroup, StyledStatusFilterDetails } from './styles';

export type ContactStatusFilterProps = {
  isOpen: boolean;
  onToggle(): void;
  value: ContactStatusFilterValue;
  onChange(value: ContactStatusFilterValue): void;
};

export const ContactStatusFilter: FC<ContactStatusFilterProps> = ({ isOpen, onToggle, value, onChange }) => {
  const { t } = useTranslation();
  const isNotEmpty = Object.values(value).some(Boolean);

  const isRevealedHalfChecked = Boolean(value.exported || value.notExported) && !(value.exported && value.notExported);

  const handleChange = (key: keyof ContactStatusFilterValue) => () => {
    onChange({ ...value, [key]: !value[key] });
  };

  const handleRevealedChange = () => {
    if (value.exported || value.notExported) {
      onChange({ ...value, exported: false, notExported: false });
    } else {
      onChange({ ...value, exported: true, notExported: true });
    }
  };

  return (
    <div data-automation="ContactStatusFilter" aria-expanded={isOpen}>
      <StyledFilterSummary isOpen={isOpen} onClick={onToggle} data-automation="ContactStatusFilterSummary">
        <StyledFilterName data-automation="ContactStatusFilterName">{t('contacts.filters.status')}</StyledFilterName>
        {isNotEmpty ? <StyledNotEmptyIndicator /> : null}
        <SWReactIcons iconName="chev-down" size="sm" />
      </StyledFilterSummary>
      <StyledStatusFilterDetails isOpen={isOpen} data-automation="ContactStatusFilterDetails">
        <Checkbox
          isChecked={value.notRevealed}
          label={t('contacts.filters.status.not_revealed')}
          onClick={handleChange('notRevealed')}
        />
        <StyledRevealedGroup data-automation="ContactStatusFilterRevealedGroup">
          <Checkbox
            isChecked={value.exported ? value.notExported : null}
            isHalfChecked={isRevealedHalfChecked}
            onClick={handleRevealedChange}
            label={t('contacts.filters.status.revealed')}
            data-automation="ContactStatusRevealed"
          />
          <StyledCheckbox
            isChecked={value.exported}
            label={t('contacts.filters.status.exported')}
            onClick={handleChange('exported')}
            data-automation="ContactStatusExported"
          />
          <StyledCheckbox
            isChecked={value.notExported}
            label={t('contacts.filters.status.not_exported')}
            onClick={handleChange('notExported')}
            data-automation="ContactStatusNotExported"
          />
        </StyledRevealedGroup>
      </StyledStatusFilterDetails>
    </div>
  );
};
